import { useState } from "react";
import AgentLayout from "@/components/layout/AgentLayout";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, DollarSign, Download, CreditCard } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, queryClient } from "@/lib/queryClient";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BookingStatus } from "@/lib/types";

export default function AgentCommissionsPage() {
  const { toast } = useToast();
  const [isWithdrawalOpen, setIsWithdrawalOpen] = useState(false);

  // Fetch agent's bookings with commissions
  const {
    data: bookings,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["agent/bookings"],
    queryFn: getQueryFn(),
  });

  // Fetch agent's account details
  const { data: agentProfile } = useQuery({
    queryKey: ["user"],
    queryFn: getQueryFn(),
  });

  // Request payout mutation
  const requestPayoutMutation = useMutation({
    mutationFn: async (data: { amount: number; notes: string }) => {
      const res = await apiRequest('POST', 'agent/payouts/request', data);
      // return await res.json();
      return res;
    },
    onSuccess: () => {
      setIsWithdrawalOpen(false);
      toast({
        title: "Withdrawal requested",
        description: "Your withdrawal request has been submitted. Our team will process it soon.",
      });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Request failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Calculate total earnings
  const getTotalEarnings = () => {
    if (!bookings || !Array.isArray(bookings)) return 0;
    
    return bookings.reduce((sum, booking: any) => {
      // Only include completed bookings
      if (booking.status === BookingStatus.COMPLETED) {
        return sum + booking.markupAmount;
      }
      return sum;
    }, 0);
  };

  // Calculate pending earnings
  const getPendingEarnings = () => {
    if (!bookings || !Array.isArray(bookings)) return 0;
    
    return bookings.reduce((sum, booking: any) => {
      // Only include confirmed but not completed bookings
      if (booking.status === BookingStatus.CONFIRMED) {
        return sum + booking.markupAmount;
      }
      return sum;
    }, 0);
  };

  // Handle withdrawal request
  const handleWithdrawalRequest = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const amount = parseFloat(formData.get("amount") as string);
    const notes = formData.get("notes") as string;

    if (isNaN(amount) || amount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid withdrawal amount.",
        variant: "destructive",
      });
      return;
    }

    const availableAmount = agentProfile?.pendingEarnings || 0;
    if (amount > availableAmount) {
      toast({
        title: "Insufficient funds",
        description: "Withdrawal amount exceeds your available earnings.",
        variant: "destructive",
      });
      return;
    }

    requestPayoutMutation.mutate({ amount, notes });
  };

  // Loading state
  if (isLoading) {
    return (
      <AgentLayout>
        <Helmet>
          <title>Commissions | StayMiddle</title>
        </Helmet>
        <div className="container py-10">
          <h1 className="text-3xl font-bold mb-8">My Commissions</h1>
          <div className="flex justify-center items-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </AgentLayout>
    );
  }

  // Error state
  if (isError) {
    return (
      <AgentLayout>
        <Helmet>
          <title>Commissions | StayMiddle</title>
        </Helmet>
        <div className="container py-10">
          <h1 className="text-3xl font-bold mb-8">My Commissions</h1>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <h3 className="text-xl font-bold mb-2">Failed to load commissions</h3>
                <p className="text-muted-foreground mb-4">
                  There was an error loading your commission data. Please try again.
                </p>
                <Button
                  onClick={() => queryClient.invalidateQueries({ queryKey: ["agent/bookings"] })}
                >
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </AgentLayout>
    );
  }

  const hasBookings = bookings && Array.isArray(bookings) && bookings.length > 0;
  const hasBankDetails = agentProfile?.ibanNumber && agentProfile?.bankName;
  const totalEarnings = getTotalEarnings();
  const pendingEarnings = getPendingEarnings();
  const availableForWithdrawal = agentProfile?.pendingEarnings || 0;
  const paidEarnings = agentProfile?.paidEarnings || 0;

  return (
    <AgentLayout>
      <Helmet>
        <title>Commissions | StayMiddle</title>
      </Helmet>
      <div className="container py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">My Commissions</h1>
            <p className="text-muted-foreground mt-1">
              Track your earnings and request withdrawals
            </p>
          </div>
        </div>

        {/* Earnings Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="bg-green-100 p-2 rounded-full">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </span>
                Total Earnings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                ${((totalEarnings + paidEarnings) / 100).toFixed(2)}
              </p>
              <p className="text-sm text-muted-foreground">Lifetime earnings</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="bg-amber-100 p-2 rounded-full">
                  <CreditCard className="h-5 w-5 text-amber-600" />
                </span>
                Available for Withdrawal
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">${(availableForWithdrawal / 100).toFixed(2)}</p>
              <p className="text-sm text-muted-foreground">Ready to withdraw</p>
            </CardContent>
            {hasBankDetails ? (
              <CardFooter>
                <Dialog open={isWithdrawalOpen} onOpenChange={setIsWithdrawalOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      disabled={availableForWithdrawal <= 0}
                    >
                      Request Withdrawal
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <form onSubmit={handleWithdrawalRequest}>
                      <DialogHeader>
                        <DialogTitle>Request Withdrawal</DialogTitle>
                        <DialogDescription>
                          Request a withdrawal to your registered bank account.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                          <Label htmlFor="amount">Amount (USD)</Label>
                          <Input
                            id="amount"
                            name="amount"
                            type="number"
                            step="0.01"
                            min="1"
                            max={(availableForWithdrawal / 100).toString()}
                            required
                            defaultValue={(availableForWithdrawal / 100).toString()}
                          />
                          <p className="text-sm text-muted-foreground">
                            Available: ${(availableForWithdrawal / 100).toFixed(2)}
                          </p>
                        </div>
                        <div className="grid gap-2">
                          <Label htmlFor="notes">Additional Notes (Optional)</Label>
                          <Textarea
                            id="notes"
                            name="notes"
                            placeholder="Any specific instructions or notes for this withdrawal"
                          />
                        </div>
                        <div className="grid gap-2">
                          <h4 className="font-medium">Payment Details</h4>
                          <div className="bg-muted p-3 rounded-md text-sm">
                            <p><strong>Bank:</strong> {agentProfile?.bankName}</p>
                            <p><strong>IBAN:</strong> {agentProfile?.ibanNumber}</p>
                            <p><strong>Swift:</strong> {agentProfile?.swiftCode || "Not provided"}</p>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Withdrawals typically process within 3-5 business days
                          </p>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsWithdrawalOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button 
                          type="submit" 
                          disabled={requestPayoutMutation.isPending}
                        >
                          {requestPayoutMutation.isPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            "Request Withdrawal"
                          )}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardFooter>
            ) : (
              <CardFooter className="flex flex-col space-y-2">
                <Button variant="outline" className="w-full" asChild>
                  <a href="/account">Add Bank Details</a>
                </Button>
                {!agentProfile?.stripeAccountId && (
                  <Button className="w-full bg-gradient-to-r from-purple-600 to-indigo-600" asChild>
                    <a href="/agent/stripe-connect">Connect with Stripe</a>
                  </Button>
                )}
              </CardFooter>
            )}
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <span className="bg-blue-100 p-2 rounded-full">
                  <Download className="h-5 w-5 text-blue-600" />
                </span>
                Paid Earnings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">${(paidEarnings / 100).toFixed(2)}</p>
              <p className="text-sm text-muted-foreground">Total paid out</p>
              {agentProfile?.lastPayout && (
                <p className="text-xs mt-2">
                  Last payout: {format(new Date(agentProfile.lastPayout), "MMM d, yyyy")}
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Commissions Table */}
        {hasBookings ? (
          <Card>
            <CardHeader>
              <CardTitle>Booking Commissions</CardTitle>
              <CardDescription>
                Your earnings from completed and upcoming bookings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableCaption>A list of your recent bookings and commissions</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking ID</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Guest</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.map((booking: any) => {
                    const bookingStatus = booking.status;
                    let statusVariant: "default" | "secondary" | "outline" | "destructive" | "success" = "outline";
                    
                    if (bookingStatus === "completed") {
                      statusVariant = "success";
                    } else if (bookingStatus === "confirmed") {
                      statusVariant = "default";
                    } else if (bookingStatus === "cancelled") {
                      statusVariant = "destructive";
                    }

                    return (
                      <TableRow key={booking.id}>
                        <TableCell className="font-medium">#{booking.id}</TableCell>
                        <TableCell>{booking.propertyTitle || `Property #${booking.propertyId}`}</TableCell>
                        <TableCell>{booking.guestName}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {format(new Date(booking.checkIn), "MMM d, yyyy")}
                            <span className="mx-1">→</span>
                            {format(new Date(booking.checkOut), "MMM d, yyyy")}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusVariant}>
                            {bookingStatus.charAt(0).toUpperCase() + bookingStatus.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-semibold">
                            ${(booking.markupAmount / 100).toFixed(2)}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <div className="mx-auto bg-muted rounded-full w-12 h-12 flex items-center justify-center mb-4">
                  <DollarSign className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-bold mb-2">No commissions yet</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  Create property links and share them with potential guests to earn commissions.
                </p>
                <Button asChild>
                  <a href="/agent/links">Create Links</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AgentLayout>
  );
}